"use client"

import React, {
  useEffect, useState, useCallback, useMemo,
} from "react"
import {
  Box, AspectRatio, Center, Spinner,
} from "@chakra-ui/react"

type Props = {
  url: string
  autoplay?: boolean
  hideControls?: boolean
}

const VideoEmbed = ({ url, autoplay = false, hideControls = false }: Props) => {
  const [aspectRatio, setAspectRatio] = useState<number>(16 / 9)
  const [embedUrl, setEmbedUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(true)

  const videoSource = useMemo(() => detectVideoSource(url), [url])
  const { type: sourceType, id: sourceId, vimeoHash } = videoSource

  const loadVideoEmbed = useCallback(
    (signal?: AbortSignal) => {
      if (sourceType === "youtube" && sourceId) {
        setEmbedUrl(
          `${YOUTUBE_EMBED_URL}${sourceId}?autoplay=${autoplay ? 1 : 0}&mute=${autoplay ? 1 : 0}&controls=${hideControls ? 0 : 1}`,
        )
        setAspectRatio(16 / 9)
        setLoading(false)

        return
      }

      if (sourceType === "vimeo" && sourceId) {
        setLoading(true)
        const vimeoId = sourceId
        const applyVimeoPlayer = (ratio: number) => {
          const vimeoQuery = buildVimeoEmbedQuery(vimeoHash, autoplay, hideControls)
          setEmbedUrl(`${VIMEO_EMBED_URL}${vimeoId}?${vimeoQuery}`)
          setAspectRatio(ratio)
        }

        fetch(`${VIMEO_API_URL}?url=${encodeURIComponent(url)}`, { signal })
          .then((res) => {
            if (!res.ok) {
              throw new Error(`Vimeo oEmbed HTTP ${res.status}`)
            }
            return res.json()
          })
          .then((data: { width?: unknown; height?: unknown }) => {
            const w = data.width
            const h = data.height
            if (typeof w === "number" && typeof h === "number" && w > 0 && h > 0) {
              applyVimeoPlayer(w / h)
            } else {
              applyVimeoPlayer(16 / 9)
            }
          })
          .catch((err: unknown) => {
            if (isAbortError(err)) {
              return
            }
            applyVimeoPlayer(16 / 9)
          })
          .finally(() => {
            if (!signal?.aborted) {
              setLoading(false)
            }
          })
      }
    },
    [url, sourceType, sourceId, vimeoHash, autoplay, hideControls],
  )

  useEffect(() => {
    const controller = new AbortController()
    loadVideoEmbed(controller.signal)
    return () => controller.abort()
  }, [loadVideoEmbed])

  return (
    <AspectRatio ratio={aspectRatio} w="100%">
      {loading || !embedUrl ? (
        <Center w="100%" h="100%">
          <Spinner size="lg" />
        </Center>
      ) : (
        <Box
          as="iframe"
          src={embedUrl}
          loading="lazy"
          border="0"
          allow="autoplay; fullscreen"
          allowFullScreen
          borderTopRadius="md"
          w="100%"
          h="100%"
        />
      )}
    </AspectRatio>
  )
}

const YOUTUBE_EMBED_URL = "https://www.youtube.com/embed/"
const VIMEO_API_URL = "https://vimeo.com/api/oembed.json"
const VIMEO_EMBED_URL = "https://player.vimeo.com/video/"
const YOUTUBE_REGEX = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/
/** Numeric id plus optional unlisted / privacy hash path segment */
const VIMEO_REGEX = /vimeo\.com\/(\d+)(?:\/([^/?#]+))?/
export const VIDEO_URL_REGEX = /^https?:\/\/(www\.)?(youtube\.com\/watch\?v=[\w-]{11}(&\S*)?|youtu\.be\/[\w-]{11}(\?\S*)?|vimeo\.com\/\d+(?:\/[^/?#]+)?(\?\S*)?)$/i

type VideoSourceType = "youtube" | "vimeo" | null

type VideoSource = {
  type: VideoSourceType
  id: string | null
  /** Privacy hash for unlisted Vimeo URLs (`vimeo.com/id/hash`) — required for embed */
  vimeoHash?: string | null
}

const isAbortError = (err: unknown): boolean => (
  (err instanceof DOMException || err instanceof Error) && (err as Error).name === "AbortError"
)

const buildVimeoEmbedQuery = (
  vimeoHash: string | null | undefined,
  autoplay: boolean,
  hideControls: boolean,
): string => {
  const params = new URLSearchParams()
  if (vimeoHash) {
    params.set("h", vimeoHash)
  }
  params.set("autoplay", autoplay ? "1" : "0")
  params.set("muted", autoplay ? "1" : "0")
  if (hideControls) {
    params.set("controls", "0")
  }
  return params.toString()
}

const detectVideoSource = (url: string): VideoSource => {
  const youtubeMatch = url.match(YOUTUBE_REGEX)
  if (youtubeMatch) {
    return {
      type: "youtube",
      id: youtubeMatch[1],
    }
  }

  const vimeoMatch = url.match(VIMEO_REGEX)
  if (vimeoMatch) {
    return {
      type: "vimeo",
      id: vimeoMatch[1],
      vimeoHash: vimeoMatch[2] ?? null,
    }
  }

  return {
    type: null,
    id: null,
  }
}

export default VideoEmbed
